import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatIDR } from "@/lib/api";
import { useAppText } from "@/contexts/AppTextContext";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, Sparkles, Tag } from "lucide-react";

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { t } = useAppText();
  const [course, setCourse] = useState(null);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/courses/${courseId}`);
        setCourse(data);
        const en = await api.get("/me/enrollments");
        if (en.data.find((e) => e.course.course_id === courseId)) setEnrolled(true);
      } catch (e) {
        toast.error("Kursus tidak ditemukan");
      }
    })();
  }, [courseId]);

  const validateVoucher = async () => {
    if (!voucherCode.trim()) return;
    try {
      const { data } = await api.post("/vouchers/validate", { code: voucherCode, course_id: courseId });
      setVoucherInfo(data);
      toast.success(`Voucher berlaku: diskon ${data.discount_percent}%`);
    } catch (e) {
      setVoucherInfo(null);
      toast.error(e.response?.data?.detail || "Voucher tidak valid");
    }
  };

  const handleEnroll = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/checkout/start", {
        course_id: courseId,
        origin_url: window.location.origin,
        voucher_code: voucherCode || undefined,
      });
      if (data.already_enrolled || data.enrolled) {
        toast.success("Berhasil terdaftar di kursus!");
        navigate(`/courses/${courseId}/learn`);
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memulai checkout");
    } finally {
      setProcessing(false);
    }
  };

  if (!course) return <div className="max-w-5xl mx-auto p-10 text-[#5C5868]">{t("dashboard.loading")}</div>;

  const finalPrice = voucherInfo
    ? Math.max(0, course.price * (100 - voucherInfo.discount_percent) / 100)
    : course.price;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="course-detail-page">
      <Link to="/catalog" className="inline-flex items-center gap-2 text-sm text-[#5C5868] hover:text-[#1A1422] mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("course_detail.back_link")}
      </Link>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="card-surface overflow-hidden mb-6">
            <div className="h-64 bg-[#F4F2F8] relative">
              {course.thumbnail && <img src={course.thumbnail} alt="" className="w-full h-full object-cover" />}
              <span className="absolute top-4 left-4 bg-black/60 backdrop-blur text-[#FF8C00] text-xs font-semibold px-3 py-1 rounded-full border border-[#FF8C00]/30">
                {course.category}
              </span>
            </div>
            <div className="p-6 sm:p-8">
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A1422] mb-3" data-testid="course-title">{course.title}</h1>
              <p className="text-[#5C5868] leading-relaxed">{course.description}</p>
              <div className="flex items-center gap-4 mt-6 text-sm text-[#5C5868]">
                <span className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> {course.lesson_count} {t("course_detail.lessons_suffix")}</span>
              </div>
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 self-start">
          <div className="card-surface p-6">
            <div className="text-xs text-[#5C5868] uppercase tracking-wider mb-1">{t("course_detail.investment_label")}</div>
            <div className="font-heading text-4xl font-extrabold text-[#1A1422] mb-1" data-testid="course-price">
              {formatIDR(finalPrice)}
            </div>
            {voucherInfo && course.price > 0 && (
              <div className="text-sm text-[#5C5868] line-through mb-3">{formatIDR(course.price)}</div>
            )}

            {enrolled ? (
              <Link to={`/courses/${courseId}/learn`} className="btn-primary w-full justify-center mt-4" data-testid="goto-learn-button">
                {t("course_detail.continue_learn")}
              </Link>
            ) : (
              <>
                <div className="mt-5">
                  <label className="text-xs text-[#5C5868] flex items-center gap-1 mb-2">
                    <Tag className="w-3.5 h-3.5" /> {t("course_detail.voucher_hint")}
                  </label>
                  <div className="flex gap-2">
                    <input
                      value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                      placeholder={t("course_detail.voucher_placeholder")}
                      className="input-field flex-1"
                      data-testid="voucher-input"
                    />
                    <button onClick={validateVoucher} className="btn-outline" data-testid="voucher-apply-button">{t("course_detail.voucher_apply")}</button>
                  </div>
                  {voucherInfo && (
                    <div className="text-xs text-[#10B981] mt-2 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> {t("course_detail.voucher_applied", { percent: voucherInfo.discount_percent })}
                    </div>
                  )}
                </div>

                <button
                  onClick={handleEnroll}
                  disabled={processing}
                  className="btn-primary w-full justify-center mt-5"
                  data-testid="enroll-button"
                >
                  {processing ? t("course_detail.processing") : finalPrice <= 0 ? t("course_detail.enroll_free") : t("course_detail.enroll_paid")}
                </button>
              </>
            )}

            <p className="text-xs text-[#8B8794] mt-4 text-center">{t("course_detail.access_note")}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
